import {
  Button,
  Divider,
  Input,
  Space,
  Table,
  TableColumnsType,
  Tooltip,
} from 'antd';
import moment from 'moment';
import { ReactNode, useEffect, useState } from 'react';
import {
  FaArrowDown,
  FaArrowUp,
  FaBusinessTime,
  FaChartArea,
  FaChartBar,
  FaChartLine,
  FaChartPie,
  FaDollarSign,
  FaDownload,
} from 'react-icons/fa';
import { FiRefreshCcw } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import styled from 'styled-components';
import { DetailBondEntity } from '../../../../entities/detailBond.entity';
import { DetenteurEntity } from '../../../../entities/detenteur.entity';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { formatNum } from '../../../../shared/constants';
import {
  getUserPortefeuille,
  getUserPortefeuilleKPI,
} from '../../../dashboard/components/Monitor/network';
import { Layout } from '../../../shared/Layout';
import { exportPortefeuilleData } from '../components/exportDataExcel';

const Container = styled.div`
  height: calc(100vh - 30px);
  display: flex;
  background-color: #1c2025;
  color: white;

  .bread-crum span {
    color: white;
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-weight: bold;
  }

  .odd-row {
    background-color: #757575;
    color: white;
  }

  .even-row {
    background-color: #acacac;
    color: white;
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: ${YELLOW} !important;
      cursor: pointer;
      color: black; /* plus lisible */
    }
  }

  .content {
    padding: 50px;
    width: 100%;
    background-color: #1c2025;
    color: white;
  }

  .kpi-box {
    height: 80px;
    width: 220px;
    box-shadow: 0 3px 5px 0 rgba(0, 0, 0, 0.3), 0 6px 20px 0 rgba(0, 0, 0, 0.3);
    padding: 10px;
    margin-right: 10px;
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: white;

    .title {
      color: white;
      font-family: Poppins-L;
    }

    .value {
      color: white;
      font-weight: bold;
      font-family: Poppins-B;
    }
  }
`;

export const ReportingPortefeuillePage = () => {
  const [portefeuilleKPI, setPortefeuilleKPI] = useState<any>();
  const [portefeuille, setPortefeuille] = useState<DetenteurEntity[]>([]);
  const [filterPorteF, setFilterPorteF] = useState<DetenteurEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const connectedUser = useSelector((state: any) => state.userReducer).user;

  useEffect(() => {
    fetchData().finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setIsLoading(true);

    await getUserPortefeuille(connectedUser.id).then(({ data }) => {
      setPortefeuille(data);
      setFilterPorteF(data);
    });

    await getUserPortefeuilleKPI(connectedUser.id).then(({ data }) => {
      setPortefeuilleKPI(data);
    });
    setIsLoading(false);
  };

  const search = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterPorteF(
      portefeuille.filter(
        (pf: any) =>
          pf.code_referent
            .toLowerCase()
            .includes(e.target.value.toLowerCase()) ||
          pf.nom.toLowerCase().includes(e.target.value.toLowerCase()) ||
          pf.prenom.toLowerCase().includes(e.target.value.toLowerCase()) ||
          pf.adresse.toLowerCase().includes(e.target.value.toLowerCase()) ||
          pf.agence.nom.toLowerCase().includes(e.target.value.toLowerCase()) ||
          pf.agence.code.toLowerCase().includes(e.target.value.toLowerCase()) ||
          pf.createur.nom
            .toLowerCase()
            .includes(e.target.value.toLowerCase()) ||
          pf.createur.prenom
            .toLowerCase()
            .includes(e.target.value.toLowerCase()) ||
          pf.detailBonds.find((p: any) =>
            p.bond.pays.toLowerCase().includes(e.target.value.toLowerCase()),
          ) ||
          pf.detailBonds.find((p: any) =>
            p.bond.code.toLowerCase().includes(e.target.value.toLowerCase()),
          ) ||
          pf.detailBonds.find((p: any) =>
            p.bond.instrument
              .toLowerCase()
              .includes(e.target.value.toLowerCase()),
          ) ||
          pf.created_at.toLowerCase().includes(e.target.value.toLowerCase()),
      ),
    );
  };

  return (
    <Layout>
      <Container>
        {/** Obtain 'current' values in Sidebar component */}
        <div className='content'>
          <h2 style={{ marginTop: 10, color: 'white' }}>KPI clés</h2>
          <Divider />

          <Space>
            <KPIContainer
              title='PNL'
              value={formatNum(portefeuilleKPI?.pnl) + ' XAF' || '--'}
              icon={<FaChartLine size={35} color='#1b263b' />}
            />
            <KPIContainer
              title='Valeur du portefeuille'
              value={
                formatNum(portefeuilleKPI?.valeurPortefeuille) + ' XAF' || '--'
              }
              icon={<FaDollarSign size={35} color='#1b263b' />}
            />
            <KPIContainer
              title='Rendement'
              value={
                formatNum(portefeuilleKPI?.rendement.toFixed(2)) + ' %' || '--'
              }
              icon={<FaChartBar size={35} color='#1b263b' />}
            />
            <KPIContainer
              title='Valeur du marché'
              value={
                formatNum(portefeuilleKPI?.valeurMarche.toFixed(2)) + ' %' ||
                '--'
              }
              icon={<FaChartArea size={35} color='#1b263b' />}
            />
            <KPIContainer
              title='Duration'
              value={
                formatNum(portefeuilleKPI?.duration.toFixed(2)) + ' ans' || '--'
              }
              icon={<FaBusinessTime size={35} color='#1b263b' />}
            />
          </Space>

          <h2 style={{ marginTop: 10, color: 'white' }}>
            <FaChartPie style={{ marginRight: 5, marginBottom: -3 }} />
            Mon Portefeuille
          </h2>
          <Divider />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 5,
            }}
          >
            <Space>
              <Input
                placeholder='Search...'
                style={{ width: 250 }}
                onChange={search}
              />
              <Tooltip title='Rafraichir' placement='top' color={YELLOW}>
                <span style={{ cursor: 'pointer' }} onClick={fetchData}>
                  <FiRefreshCcw color='black' />
                </span>
              </Tooltip>
              <PulseLoader
                size={5}
                margin={2}
                color={YELLOW}
                loading={isLoading}
              />
            </Space>

            <Space>
              <Button
                icon={
                  <FaDownload style={{ marginBottom: -2, marginRight: 5 }} />
                }
                className='butt'
                style={{ borderRadius: 5 }}
                onClick={() => exportPortefeuilleData(portefeuille)}
              >
                Exporter
              </Button>
            </Space>
          </div>

          <Table
            loading={isLoading}
            columns={COLUMNS}
            dataSource={filterPorteF}
            rowClassName={(row, index) =>
              index % 2 === 0 ? 'even-row' : 'odd-row'
            }
            expandable={{
              expandedRowRender: (row) => {
                return (
                  <Table<DetailBondEntity>
                    columns={SUB_COLUMNS}
                    dataSource={(row as any)?.detailBonds}
                    rowKey='id'
                    rowClassName={(row, index) =>
                      index % 2 === 0 ? 'even-row' : 'odd-row'
                    }
                    size='small'
                    style={{ zoom: 0.9 }}
                    pagination={false}
                  />
                );
              },
            }}
            rowKey='id'
            size='small'
            style={{ zoom: 0.9 }}
            pagination={false}
          />
        </div>
      </Container>
    </Layout>
  );
};

const KPIContainer = ({
  title,
  value,
  icon,
}: {
  title: string;
  value: string;
  icon: ReactNode;
}) => {
  return (
    <div
      className='kpi-box'
      style={{
        backgroundColor: YELLOW,
        color: 'white',
      }}
    >
      <div
        style={{
          color: 'white',
        }}
      >
        {icon}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}
      >
        <div className='title'>{title}</div>
        <div className='value'>{value}</div>
      </div>
    </div>
  );
};

const COLUMNS = [
  {
    title: 'Code client',
    dataIndex: 'code_referent',
  },
  {
    title: 'Détenteur',
    render: (cell: any, row: DetenteurEntity) => `${row.nom} ${row.prenom}`,
  },
  {
    title: 'Adresse',
    dataIndex: 'adresse',
  },
  {
    title: 'Email',
    dataIndex: 'email',
  },
  {
    title: 'Contact',
    dataIndex: 'mobile',
  },
  {
    title: 'Code agence',
    render: (cell: any, row: any) => `${row.agence.code}`,
  },
  {
    title: 'Agence',
    render: (cell: any, row: any) => `${row.agence.nom}`,
  },
  {
    title: 'Gestionnaire',
    render: (cell: any, row: any) =>
      `${row.createur.nom} ${row.createur.prenom}`,
  },
  {
    title: 'Qte total titre',
    render: (cell: any, row: any) => `${formatNum(row.qteTotal)}`,
  },
  {
    title: 'Montant total',
    render: (cell: any, row: any) => `${formatNum(row.montantTotal)}`,
  },
  {
    title: 'P&L',
    dataIndex: 'pnl',
    render: (cell: any) => (
      <b style={{ color: cell > 0 ? '#008000' : '#e57373' }}>
        {cell > 0 ? (
          <FaArrowUp color='#008000' />
        ) : (
          <FaArrowDown color='#e57373' />
        )}{' '}
        {formatNum(cell)}
      </b>
    ),
    align: 'right' as const,
  },
];

const SUB_COLUMNS: TableColumnsType<DetailBondEntity> = [
  {
    title: 'Pays',
    render: (cell: any, row: DetailBondEntity) => row.bond.pays,
  },
  {
    title: 'Instrument',
    render: (cell: any, row: DetailBondEntity) => row.bond.instrument,
  },
  {
    title: 'Code Titre',
    render: (cell: any, row: DetailBondEntity) => row.parentCode,
  },
  {
    title: 'Date acquisition',
    dataIndex: 'dateAcquisition',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Date attribution',
    dataIndex: 'created_at',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Date maturité',
    render: (cell: any, row: DetailBondEntity) =>
      `${moment(row.bond.dateMaturite).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Qte',
    dataIndex: 'qte',
  },
  {
    title: 'Prix',
    dataIndex: 'prixNominale',
    render: (cell: any) => `${formatNum(cell * 1)}`,
  },
  {
    title: 'Montant',
    dataIndex: 'montant',
    render: (cell: any) => `${formatNum(cell * 1)}`,
  },
  {
    title: 'Décote',
    dataIndex: 'decote',
    render: (cell: any) => <span>{cell} %</span>,
  },
  {
    title: 'Coupon',
    dataIndex: 'tauxInteret',
    render: (cell: any) => <span>{cell} %</span>,
  },
  {
    title: 'Rendement',
    dataIndex: 'tauxRendement',
    render: (cell: any) => <span>{cell} %</span>,
  },
  {
    title: 'Fréquence',
    dataIndex: 'frequence',
  },
  {
    title: 'P&L',
    dataIndex: 'pnl',
    render: (cell: any) => (
      <b style={{ color: cell > 0 ? '#008000' : '#e57373' }}>
        {cell > 0 ? (
          <FaArrowUp color='#008000' />
        ) : (
          <FaArrowDown color='#e57373' />
        )}{' '}
        {formatNum(cell)}
      </b>
    ),
    align: 'right' as const,
  },
];
